/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import useAdvanceTable from 'hooks/useAdvanceTable';
import SearchBox from 'components/common/SearchBox';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import productSearchService from 'service/productSearch';
import Button from 'components/base/Button';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';

export type SearchProduct = {
  name: string;
  variation: string;
  barcode: string;
  original_barcode: string;
  unit: string;
  quantity: string;
  nomenclature_id: string;
  nomenclature_pid: string;
  currency_id: string;
  currency_pid: string;
  price_in: string;
  guid: string;
  gupid: string;
  measure_unit_kind_id: string;
  measure_unit_id: string;
  measure_unit_pid: string;
  package_quantity: string;
};

interface SearchProductInventoryProps {
  setOpenSearch: (show: boolean) => void;
  setFocusToInput: (show: boolean) => void;
  reset?: any;
  setSearchedValue?: any;
  tableData?: any;
}

const SearchProductInventory = ({
  setOpenSearch,
  setFocusToInput,
  reset,
  setSearchedValue,
  tableData
}: SearchProductInventoryProps) => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { warehouse_pid } = state;
  const [searchValue, setSearchValue] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const inputSearchRef = useRef<HTMLInputElement | any>();

  const { data, refetch, isFetching, isLoading } = useQuery(
    ['GET_PRODUCT', searchValue, currentPage],
    () => {
      if (warehouse_pid) {
        const res = productSearchService
          .getListProduct({
            offset: pageToOffset(currentPage, 10),
            limit: 10,
            warehouse_pid: warehouse_pid,
            name: searchValue
          })
          .then((res: any) => {
            setPageCount(Math.ceil(res?.count / 10));
            return res?.products;
          });
        return res;
      }
    },
    { enabled: !!currentPage || !searchValue || searchValue?.length > 2 }
  );

  const loadingData = isLoading || isFetching;

  const generateData: SearchProduct[] = useMemo(() => {
    return (
      data?.map((items: any) => {
        const packageQuantity = items?.package_quantity || 1;

        const totalQuantity = items?.total_quantity
          ? Number(items?.total_quantity.toFixed(3))
          : 0;

        const fullPackages = Math.floor(totalQuantity / packageQuantity);
        const remainingQuantity = totalQuantity % packageQuantity;
        const quantityDisplayTotal = `${fullPackages}/${remainingQuantity}`;

        return {
          name: items?.name,
          variation: items?.nomenclature?.is_variation ? 'ДА' : 'НЕТ',
          barcode: items?.barcode,
          original_barcode: items?.nomenclature?.barcode_original,
          unit: items?.nomenclature?.measure_unit?.name?.ru,
          quantity:
            items?.nomenclature?.measure_unit_kind_pid === 1
              ? quantityDisplayTotal
              : items?.total_quantity,
          nomenclature_id: items?.nomenclature_id,
          nomenclature_pid: items?.nomenclature_pid || 0,
          currency_id: items?.currency_id,
          currency_pid: items?.currency_pid || 0,
          price: items?.price ?? 0,
          price_in: items?.price_in ?? 0,
          guid: items?.id,
          gupid: items?.pid || 0,
          measure_unit_kind: items?.nomenclature?.measure_unit_kind_id,
          measure_unit_id: items?.nomenclature?.measure_unit?.id,
          measure_unit_pid: items?.nomenclature?.measure_unit?.pid || 0,
          package_quantity: items?.package_quantity || 1
        };
      }) ?? []
    );
  }, [data, searchValue]);

  const searchProductTableColumns: ColumnDef<SearchProduct>[] = [
    {
      id: 'action',
      cell: rowData => {
        const createdData = {
          product_name: rowData?.row?.original?.name,
          product_id: rowData?.row?.original?.guid,
          product_pid: rowData?.row?.original?.gupid,
          scan: rowData?.row?.original?.barcode,
          current_inventory_balance:
            tableData[rowData?.row?.original?.barcode]?.quantity_fact ?? 0,
          current_balance: rowData?.row?.original?.quantity ?? 0,
          nomenclature_id: rowData?.row?.original?.nomenclature_id,
          nomenclature_pid: rowData?.row?.original?.nomenclature_pid,
          currency_id: rowData?.row?.original?.currency_id,
          currency_pid: rowData?.row?.original?.currency_pid,
          price_in: rowData?.row?.original?.price_in ?? 0,
          measure_unit_kind: rowData?.row?.original?.measure_unit_kind_id,
          measure_unit_id: rowData?.row?.original?.measure_unit_id,
          measure_unit_pid: rowData?.row?.original?.measure_unit_pid,
          package_quantity: rowData?.row?.original.package_quantity || 1
        };

        return (
          <div className="d-flex gap-2">
            <Button
              variant="hover"
              className="p-2"
              onClick={() => {
                reset(createdData);
                setSearchedValue(`${rowData?.row?.original?.barcode}`);
                setOpenSearch(false);
                setFocusToInput(true);
              }}
            >
              <FeatherIcon icon="plus" className="cursor-pointer" size={20} />
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '7%' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'variation',
      header: t('variation'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'original_barcode',
      header: t('barcode_original'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price',
      header: t('selling_price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: generateData,
    columns: searchProductTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if (searchValue?.length > 2 || searchValue === '') {
      refetch();
    }
  }, [searchValue]);

  useEffect(() => {
    if (inputSearchRef.current) {
      inputSearchRef.current.focus();
    }
  }, [inputSearchRef]);
  return (
    <div>
      <div className="mb-4">
        <SearchBox
          placeholder={t('search')}
          onChange={e => setSearchValue(e?.target?.value)}
          ref={inputSearchRef}
        />
      </div>
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          isLoading={loadingData}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </AdvanceTableProvider>
    </div>
  );
};

export default SearchProductInventory;
